import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import { getCompanyLogo } from "../services/api";
import { companyLogoTitleStyle, COMPANY_LOGO_TITLE_SIZE_MOBILE } from "../utils/chartCompanyLogo";

try { HighchartsMore(Highcharts); } catch {}

const CHART_THEME = {
  bg: "#1a1a24",
  text: "#e8e8f0",
  muted: "#888898",
  grid: "#2a2a3a",
  hist: "#6366f1",
  future: "#f59e0b",
  line: "#10b981",
  ci: "rgba(16, 185, 129, 0.18)",
};

export default function PriceForecastRegressionChart({ title, xLabel, yLabel, chartData, ticker }) {
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const sym = String(ticker || "").trim().toUpperCase();
    setCompanyLogoUrl(null);
    if (!sym) return undefined;
    getCompanyLogo(sym).then((url) => {
      if (!cancelled) setCompanyLogoUrl(url);
    });
    return () => { cancelled = true; };
  }, [ticker]);

  const options = useMemo(() => {
    if (!chartData?.historical?.length) return null;
    return {
      chart: {
        type: "scatter",
        height: 300,
        backgroundColor: CHART_THEME.bg,
        style: { fontFamily: "inherit" },
        zooming: { type: "xy" },
      },
      title: { text: null },
      credits: { enabled: false },
      legend: {
        itemStyle: { color: CHART_THEME.text },
        itemHoverStyle: { color: CHART_THEME.text },
      },
      xAxis: {
        title: { text: xLabel, style: { color: CHART_THEME.muted } },
        labels: { style: { color: CHART_THEME.muted } },
        gridLineColor: CHART_THEME.grid,
        lineColor: CHART_THEME.grid,
      },
      yAxis: {
        title: { text: yLabel, style: { color: CHART_THEME.muted } },
        labels: { style: { color: CHART_THEME.muted } },
        gridLineColor: CHART_THEME.grid,
        lineColor: CHART_THEME.grid,
      },
      tooltip: {
        shared: true,
        backgroundColor: "#22222f",
        borderColor: CHART_THEME.grid,
        style: { color: CHART_THEME.text },
        pointFormat: "<b>{series.name}</b><br/>{point.x:.2f} → ${point.y:.2f}",
      },
      plotOptions: {
        series: { animation: false },
        scatter: { marker: { radius: 4 } },
        arearange: { enableMouseTracking: false },
      },
      series: [
        {
          name: "95% CI",
          type: "arearange",
          data: chartData.ciBand || [],
          color: CHART_THEME.ci,
          fillOpacity: 1,
          lineWidth: 0,
          zIndex: 0,
        },
        {
          name: "Regression",
          type: "line",
          data: chartData.regressionLine || [],
          color: CHART_THEME.line,
          lineWidth: 2,
          enableMouseTracking: false,
          marker: { enabled: false },
          zIndex: 1,
        },
        {
          name: "Historical",
          type: "scatter",
          data: chartData.historical,
          color: CHART_THEME.hist,
          zIndex: 2,
        },
        ...(chartData.future?.length
          ? [{
              name: "Forward estimates (next 8 qtrs)",
              type: "scatter",
              data: chartData.future,
              color: CHART_THEME.future,
              marker: { symbol: "diamond", radius: 5 },
              zIndex: 3,
            }]
          : []),
      ],
    };
  }, [chartData, xLabel, yLabel]);

  if (!options) return null;
  return (
    <div>
      {(title || companyLogoUrl) ? (
        <div
          style={{
            fontWeight: 600,
            marginBottom: 6,
            color: CHART_THEME.text,
            display: "flex",
            alignItems: "center",
            gap: 10,
            paddingLeft: "2ch",
          }}
        >
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt=""
              style={companyLogoTitleStyle(COMPANY_LOGO_TITLE_SIZE_MOBILE)}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : null}
          {title ? <span>{title}</span> : null}
        </div>
      ) : null}
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
}
